
import React, { useState } from 'react'
import { withComponentContext } from 'widgets-shared/core'
import { compose } from 'widgets-shared/core'
import { alpha } from '@mui/material/styles'
import { withTranslations } from 'widgets-shared/core'
import TopBar from './TopBar/TopBar'
import CompetitionsTabBar from './CompetitionsTabBar/CompetitionsTabBar'
import GridFilters from './GridFilters/GridFilters'
import OddsGrid from './OddsGrid/OddsGrid'
import FooterPagination from 'smartstream-widgets/controls/FooterPagination/FooterPagination'
import MissingLink from 'smartstream-widgets/controls/MissingLink/MissingLink'
import FixtureCardsList from './FixtureCardsList/FixtureCardsList'
import { competitionTranslations } from 'smartstream-widgets/constants/competitions'
import TermsOfService from './TermsOfService/TermsOfService'
import Disclaimer from 'smartstream-widgets/controls/Disclaimer/Disclaimer'
import { Grid, CircularProgress, Typography } from '@mui/material'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { OddsFormat, OddsComparisonGridConfig, Data, AvailableCompetition } from './types'
import { TranslationFunction, Bookmakers } from 'smartstream-widgets/types/sharedModule/core'
import { MarketType } from 'smartstream-widgets/types/market'
import OddsByHeader from './OddsByHeader/OddsByHeader'
import { withComponentContextProvider } from 'widgets-shared/core'
import { PropTranslations } from 'smartstream-widgets/types/widget'

type Props = {
  bookmakers: Bookmakers,
  isBookmakerLinkEnabled: boolean,
  classes: {
    [K in string]: string
  },
  config: OddsComparisonGridConfig,
  t: TranslationFunction,
  availableCompetitions: Array<AvailableCompetition>,
  activeTab?: string,
  activePage: string,
  disclaimer?: string,
  footerLink?: string,
  lastUpdateAction: string,
  tabChange: ((arg0: string) => void),
  marketTypeHandler: ((marketType: MarketType) => void),
  paginationHandler: ((nextPage: number) => void),
  isLoading: boolean,
  data: Data,
  mobileView: boolean,
  isMotorSport: boolean,
  translations: PropTranslations
}

const styles = (theme: Theme) => createStyles({
  competitionTabBar: {
    width: '100%'
  },
  oddsFilter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  oddsBy: {
    display: 'none'
  },
  gridFilters: {
    margin: `30px 0px`
  },
  oddsGrid: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0px 0px`,
    boxShadow: `0px 1px 3px 0px ${alpha(theme.palette.common.black, 0.2)}`
  },
  root: {
    backgroundColor: theme.palette.background.widget || theme.palette.background.default
  },
  content: {
    padding: `0px 23px 50px`
  },
  contentMobile: {
    padding: 0
  },
  gridContent: {
    padding: `0px 20px`
  },
  pag: {
    padding: `0px 20px`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: `0px 0px ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
    boxShadow: `0px 1px 3px 0px ${alpha(theme.palette.common.black, 0.2)}`,
    justifyContent: 'flex-end',
    height: 55
  },
  pagMobile: {
    padding: `0px 15px`,
    height: 60
  },
  termsOfService: {
    paddingTop: 24,
    width: 'auto'
  },
  disclaimerRoot: {
    justifyContent: 'flex-end',
    paddingTop: 26,
    width: 'auto'
  },
  disclaimer: {
    fontSize: 9,
  },
  appBar: {},
  termsMobile: {
    flexDirection: 'column',
    textAlign: 'center',
    '& $termsOfService': {
      paddingTop: 0,
      justifyContent: 'center',
      '& svg': {
        width: 18
      },
      '& p': {
        fontSize: 11
      }
    },
    '& $disclaimer': {
      fontSize: 8,
    },
    '& $disclaimerRoot': {
      fontSize: 8,
      justifyContent: 'center',
      padding: `10px`
    }
  },
  loadingText: {
    color: theme.palette.primary.main,
    fontSize: 14,
    marginTop: 0
  },
  '@keyframes ellipsis-dot': {
    '0%': { opacity: 0 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0 }
  },
  animatedEllipsis: {
    '& span': {
      animation: '$ellipsis-dot 2s infinite',
      opacity: 0,
      '&:nth-child(2)': {
        animationDelay: '0.1s'
      },
      '&:nth-child(3)': {
        animationDelay: '0.2s'
      }
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    height: '50vh'
  }
})

export function OddsComparisonGridComponent(props: Props) {
  const {
    availableCompetitions,
    activeTab,
    activePage,
    bookmakers = {},
    isBookmakerLinkEnabled,
    disclaimer,
    footerLink,
    lastUpdateAction,
    classes,
    tabChange,
    config: {
      oddsFormat: configOddsFormat = 'american',
      initialMarketType = 'moneyLine',
      BASE_ASSET_PATH = ''
    },
    data,
    isLoading,
    marketTypeHandler,
    paginationHandler,
    t,
    mobileView,
    isMotorSport,
    translations
  } = props

  const [oddsFormat, setOddsFormat] = useState(configOddsFormat)
  const competitions = competitionTranslations(t)
  const competitionTitleTranslate = activeTab && competitions[activeTab] || activeTab
  const isTabToday = activeTab === 'today'
  const dataNode = isTabToday ? data.competitions : data.fixtures
  const dataAvailable = dataNode && dataNode.length > 0
  const pagination = data.pagination || null
  const activeElement = availableCompetitions.find(cp => cp.competitionId === activeTab)
  const activeMarketTypes = (activeElement && activeElement.marketTypes) || []
  const competitionIds = availableCompetitions.map(cp => cp.competitionId)

  return (
    <div className={classes.root}>
      <TopBar
        mobileView={mobileView}
        classes={{ appBar: classes.appBar }}
        competitionId={activeTab}
        competitionName={competitionTitleTranslate}
        oddFormatHandler={(newValue: OddsFormat) => setOddsFormat(newValue)}
      />
      {
        availableCompetitions.length > 1 &&
        <CompetitionsTabBar
          mobileView={mobileView}
          availableCompetitions={competitionIds}
          classes={{ root: classes.competitionTabBar }}
          onTabChange={tabChange}
          competitionId={activeTab}
        />
      }
      {isLoading ?
        <div className={classes.loadingContainer}><CircularProgress />
          <Typography className={classes.loadingText}>
            {translations.loading}
            <span className={classes.animatedEllipsis}>
              <span>.</span><span>.</span><span>.</span>
            </span>
          </Typography>
        </div>
        : (dataAvailable ?
          <div className={`${mobileView ? classes.contentMobile : classes.content}`}>
            {(!isMotorSport && mobileView) ?
              <>
                <OddsByHeader classes={{ root: classes.oddsBy }}></OddsByHeader>
                <FooterPagination
                  fullPage={mobileView}
                  classes={{ root: classes.pagMobile }}
                  clickHandler={paginationHandler}
                  itemsPerPage={pagination && pagination.itemsPerPage}
                  page={pagination && pagination.page}
                  totalResults={pagination && pagination.totalResults}
                  BASE_ASSET_PATH={BASE_ASSET_PATH} />
                <FixtureCardsList
                  bookmakers={bookmakers}
                  isBookmakerLinkEnabled={isBookmakerLinkEnabled}
                  data={dataNode}
                  oddsFormat={oddsFormat}
                  competitionId={activeTab}
                  lastUpdateAction={lastUpdateAction}
                  page={activePage} />
              </> :
              <>
                <div className={classes.oddsFilter}>
                  <OddsByHeader classes={{ root: classes.oddsBy }}></OddsByHeader>
                  <GridFilters
                    activeTab={activeTab}
                    classes={{ root: classes.gridFilters }}
                    marketTypes={activeMarketTypes}
                    disableLiveGamesSwitch
                    initialMarketType={initialMarketType}
                    marketTypeHandler={marketTypeHandler} />
                </div>
                <OddsGrid
                  bookmakers={bookmakers}
                  isBookmakerLinkEnabled={isBookmakerLinkEnabled}
                  classes={{ root: classes.oddsGrid }}
                  dataNodes={dataNode}
                  inView={activeTab}
                  oddsFormat={oddsFormat}
                  showMoreHandler={tabChange} />
              </>
            }
            <FooterPagination
              fullPage={mobileView}
              classes={{ root: `${mobileView ? classes.pagMobile : classes.pag}` }}
              clickHandler={paginationHandler}
              itemsPerPage={pagination && pagination.itemsPerPage}
              page={pagination && pagination.page}
              totalResults={pagination && pagination.totalResults}
              BASE_ASSET_PATH={BASE_ASSET_PATH}
            />
            <Grid className={`${mobileView ? classes.termsMobile : ''}`} container justifyContent={'space-between'}>
              <TermsOfService
                classes={{ root: classes.termsOfService }} />
              <Disclaimer
                disclaimer={disclaimer}
                footerLink={footerLink}
                classes={{ disclaimerText: classes.disclaimer, root: classes.disclaimerRoot }} />
            </Grid>
          </div>
          : <MissingLink clickHandler={tabChange} mobileView={mobileView} />)
      }
    </div>
  )
}

const OddsComparisonGridComponentComposed = compose(
  withComponentContext,
  withStyles(styles, { name: 'OddsComparisonGridComponent' }),
  withTranslations((t: TranslationFunction) => {
    return {
      today: t('competitions.today.translation', {
        defaultValue: 'Today\'s',
        context: 'label tab for today inside Odds Comparison Grid widget'
      }),
      loading: t('loading.translation', {
        defaultValue: 'Loading games', context: 'Text to be used in the loader animation inside Odds Comparison Grid widget'
      })
    }
  })
)(OddsComparisonGridComponent) as React.ElementType

export default OddsComparisonGridComponentComposed

export const OddsComparisonGridComponentWithContext = withComponentContextProvider(OddsComparisonGridComponentComposed) as React.Component

